import type { TranslationKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';

import { CONTACT_DECISION_MAKER_REASON_IDS } from '#src/app/shared/utils/contactDecisionMaker';
import type { ContactDecisionMakerReasonId } from '#src/app/shared/utils/contactDecisionMaker';

type ReasonCard = {
	id: ContactDecisionMakerReasonId;
	/**
	 * Decorative, and kept out of the translated string on purpose — an emoji inside the YAML is
	 * something translators have to carry, and the SPH painted door already logged that as a mistake
	 * worth not repeating.
	 */
	emoji: string;
	labelKey: TranslationKey;
	/** Screen 3's heading. The ask narrows to whatever the supporter picked on screen 2. */
	headingKey: TranslationKey;
	/** Absent for `own`: the spec gives that card "just a blank page and your words". */
	promptKey?: TranslationKey;
	placeholderKey: TranslationKey;
	/** Screen 3's "You could mention…" talking points. Three per reason, `own` included. */
	pointsKeys: readonly [TranslationKey, TranslationKey, TranslationKey];
};

/**
 * The five reason cards, keyed by id so a lookup is total and callers need no `undefined` branch.
 *
 * Every key is spelled out in full: the i18n build plugin statically extracts `translationKey()`
 * arguments, and a template literal that opens with an interpolation gives it no static prefix to
 * match on — it fails the build with "Translation key pattern is too generic".
 */
const REASON_CARD_BY_ID: Readonly<Record<ContactDecisionMakerReasonId, ReasonCard>> = {
	affects: {
		id: 'affects',
		emoji: '✋',
		labelKey: translationKey('corgi.contact_decision_maker.story.reasons.affects.label'),
		headingKey: translationKey('corgi.contact_decision_maker.story.headings.affects'),
		promptKey: translationKey('corgi.contact_decision_maker.story.reasons.affects.prompt'),
		placeholderKey: translationKey('corgi.contact_decision_maker.story.reasons.affects.placeholder'),
		pointsKeys: [
			translationKey('corgi.contact_decision_maker.story.points.affects.first'),
			translationKey('corgi.contact_decision_maker.story.points.affects.second'),
			translationKey('corgi.contact_decision_maker.story.points.affects.third'),
		],
	},
	care: {
		id: 'care',
		emoji: '💛',
		labelKey: translationKey('corgi.contact_decision_maker.story.reasons.care.label'),
		headingKey: translationKey('corgi.contact_decision_maker.story.headings.care'),
		promptKey: translationKey('corgi.contact_decision_maker.story.reasons.care.prompt'),
		placeholderKey: translationKey('corgi.contact_decision_maker.story.reasons.care.placeholder'),
		pointsKeys: [
			translationKey('corgi.contact_decision_maker.story.points.care.first'),
			translationKey('corgi.contact_decision_maker.story.points.care.second'),
			translationKey('corgi.contact_decision_maker.story.points.care.third'),
		],
	},
	community: {
		id: 'community',
		emoji: '🏡',
		labelKey: translationKey('corgi.contact_decision_maker.story.reasons.community.label'),
		headingKey: translationKey('corgi.contact_decision_maker.story.headings.community'),
		promptKey: translationKey('corgi.contact_decision_maker.story.reasons.community.prompt'),
		placeholderKey: translationKey('corgi.contact_decision_maker.story.reasons.community.placeholder'),
		pointsKeys: [
			translationKey('corgi.contact_decision_maker.story.points.community.first'),
			translationKey('corgi.contact_decision_maker.story.points.community.second'),
			translationKey('corgi.contact_decision_maker.story.points.community.third'),
		],
	},
	right: {
		id: 'right',
		emoji: '⚖️',
		labelKey: translationKey('corgi.contact_decision_maker.story.reasons.right.label'),
		headingKey: translationKey('corgi.contact_decision_maker.story.headings.right'),
		promptKey: translationKey('corgi.contact_decision_maker.story.reasons.right.prompt'),
		placeholderKey: translationKey('corgi.contact_decision_maker.story.reasons.right.placeholder'),
		pointsKeys: [
			translationKey('corgi.contact_decision_maker.story.points.right.first'),
			translationKey('corgi.contact_decision_maker.story.points.right.second'),
			translationKey('corgi.contact_decision_maker.story.points.right.third'),
		],
	},
	own: {
		id: 'own',
		emoji: '✏️',
		labelKey: translationKey('corgi.contact_decision_maker.story.reasons.own.label'),
		headingKey: translationKey('corgi.contact_decision_maker.story.headings.own'),
		placeholderKey: translationKey('corgi.contact_decision_maker.story.reasons.own.placeholder'),
		pointsKeys: [
			translationKey('corgi.contact_decision_maker.story.points.own.first'),
			translationKey('corgi.contact_decision_maker.story.points.own.second'),
			translationKey('corgi.contact_decision_maker.story.points.own.third'),
		],
	},
};

/**
 * Spec display order (§Screen 2). Derived from the id tuple rather than written out again, so the
 * order and the card set cannot drift apart. `own` is last and renders differently — a full-width
 * horizontal tile rather than one of the stacked cards.
 */
export const REASON_CARDS: readonly ReasonCard[] = CONTACT_DECISION_MAKER_REASON_IDS.map((id) => REASON_CARD_BY_ID[id]);

/** Total by construction: every id in the union has a card. */
export function findReasonCard(id: ContactDecisionMakerReasonId): ReasonCard {
	return REASON_CARD_BY_ID[id];
}
